import { useCallback, useRef } from 'react';

/**
 * 가로 스크롤 영역을 마우스로 끌어서 움직이게 한다.
 *
 * 데스크톱 브라우저는 가로 스크롤 컨테이너를 드래그로 밀 수 없다(휠은 Shift+휠).
 * 터치는 이미 네이티브로 잘 되므로 마우스일 때만 개입한다.
 *
 * 살짝 움직인 정도는 클릭으로 보고, 임계값(5px)을 넘겨 끌었을 때만
 * 이어지는 click 을 한 번 막아 칩이 눌리는 것을 방지한다.
 */
const DRAG_THRESHOLD = 5;

export const useDragScroll = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const state = useRef({ active: false, startX: 0, startLeft: 0, moved: false });

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || !ref.current) return;
    state.current = {
      active: true,
      startX: e.clientX,
      startLeft: ref.current.scrollLeft,
      moved: false,
    };
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const s = state.current;
    if (!s.active || !ref.current) return;
    const dx = e.clientX - s.startX;
    if (!s.moved && Math.abs(dx) < DRAG_THRESHOLD) return;
    s.moved = true;
    // 드래그 중 텍스트 선택이 잡히지 않도록
    e.preventDefault();
    ref.current.scrollLeft = s.startLeft - dx;
  }, []);

  const end = useCallback(() => {
    state.current.active = false;
  }, []);

  // 끌고 난 뒤의 클릭만 삼킨다(그냥 클릭은 그대로 통과).
  const onClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (state.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      state.current.moved = false;
    }
  }, []);

  return {
    ref,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: end,
      onPointerLeave: end,
      onClickCapture,
    },
  };
};

export default useDragScroll;
